"use client";
import React, { useEffect, useRef, useState } from "react";

interface Testimonial {
  quote: string;
  author: string;
  role: string;
  location: string;
  result: string;
}

const TESTIMONIALS: Testimonial[] = [
  {
    quote: "I had been looking at Kashmir land for two years but never trusted anyone enough to move forward. Malik Luxury Estates was different. They showed me exactly what I was buying, walked me through every document, and never pressured me once. I now own a plot in Pahalgam that has already appreciated 22% in 18 months.",
    author: "Arjun Sharma",
    role: "Software Engineer, Bengaluru",
    location: "Pahalgam Plot · ₹48 Lakhs",
    result: "+22% in 18 months",
  },
  {
    quote: "As an NRI based in Dubai, I was nervous about investing in land back home. The team handled everything remotely — site visit videos, legal checks, registration coordination. I didn't have to fly back once. The process was cleaner than any property deal I have done in the UAE.",
    author: "Faisal Mir",
    role: "NRI Investor, Dubai",
    location: "Budgam Plot · ₹32 Lakhs",
    result: "Fully remote purchase",
  },
  {
    quote: "I invested ₹20 Lakhs in a Sopore plot on their recommendation. I was a first-time investor and had no idea where to start. They explained everything — the legal process, the growth potential, the risks. Two years later, the land is worth ₹31 Lakhs. I am already looking at my second investment.",
    author: "Priya Nanda",
    role: "Marketing Manager, Delhi",
    location: "Sopore Plot · ₹20 Lakhs",
    result: "₹20L → ₹31L in 2 years",
  },
];

const FAQS = [
  {
    question: "Is it safe to invest in land in Kashmir?",
    answer: "Yes. The legal and political environment in J&K has stabilised significantly since 2019. Land transactions are governed by standard Indian property laws. We only recommend plots with clear titles and no legal disputes.",
  },
  {
    question: "How do you verify that a property is legally clear?",
    answer: "Every property we list goes through a title search, encumbrance certificate check, and revenue records verification. We work with registered local lawyers to ensure there are no hidden liabilities before we present a plot to you.",
  },
  {
    question: "What is the minimum budget to invest?",
    answer: "You can start with as little as ₹15 Lakhs in high-growth zones like Sopore or Budgam. Premium plots in Pahalgam and Gulmarg start from ₹45 Lakhs. We have options across budgets.",
  },
  {
    question: "Can I visit the site before buying?",
    answer: "Absolutely. We arrange guided site visits for all serious buyers. For NRIs or buyers who cannot travel, we provide detailed video walkthroughs, drone footage, and a live virtual tour with our on-ground team.",
  },
  {
    question: "What is the process from enquiry to ownership?",
    answer: "It is a four-step process: consultation, property shortlisting, site visit and due diligence, and finally deal closure with registration support. Most deals are completed within 30–60 days of the initial enquiry.",
  },
];

export default function Testimonials() {
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("visible");
        });
      },
      { threshold: 0.1 }
    );
    cardRefs.current.forEach((card) => { if (card) observer.observe(card); });
    return () => observer.disconnect();
  }, []);

  return (
    <>
      {/* Testimonials */}
      <section className="bg-fog/30 py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs font-medium tracking-[0.3em] uppercase text-pine-light mb-4 block">
              Client Stories
            </span>
            <h2 className="font-serif text-display-sm text-pine font-light">
              Real investors. <em className="italic">Real returns.</em>
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, index) => (
              <div
                key={t.author}
                ref={(el) => { cardRefs.current[index] = el; }}
                className="reveal bg-linen rounded-3xl p-8 shadow-card"
                style={{ transitionDelay: `${index * 0.15}s` }}>

                <blockquote className="pull-quote mb-6">
                  <p className="font-serif italic text-pine font-light leading-relaxed text-base">
                    &ldquo;{t.quote}&rdquo;
                  </p>
                </blockquote>

                <div className="pt-4 border-t border-fog/60">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-pine text-sm">{t.author}</p>
                      <p className="text-xs text-pine-light mt-0.5">{t.role}</p>
                      <p className="text-xs text-pine-light/70 mt-1">{t.location}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-amber font-medium">{t.result}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Stats bar */}
          <div className="mt-16 text-center">
            <div className="inline-flex items-center gap-6 bg-linen rounded-2xl px-8 py-5 shadow-card flex-wrap justify-center">
              <div className="text-center">
                <div className="font-serif text-3xl font-light text-pine">500+</div>
                <div className="text-xs text-pine-light mt-1">Satisfied investors<br />across India & abroad</div>
              </div>
              <div className="w-px h-12 bg-fog hidden sm:block" />
              <div className="text-center">
                <div className="font-serif text-3xl font-light text-amber">18–25%</div>
                <div className="text-xs text-pine-light mt-1">Average annual appreciation<br />in prime Kashmir zones</div>
              </div>
              <div className="w-px h-12 bg-fog hidden sm:block" />
              <div className="text-center">
                <div className="font-serif text-3xl font-light text-pine">100%</div>
                <div className="text-xs text-pine-light mt-1">Legally verified<br />listings only</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="bg-linen py-24 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs font-medium tracking-[0.3em] uppercase text-pine-light mb-4 block">
              Common Questions
            </span>
            <h2 className="font-serif text-display-sm text-pine font-light">
              Everything you need <em className="italic">to know.</em>
            </h2>
          </div>

          <div className="space-y-3">
            {FAQS.map((faq, index) => (
              <div key={index} className="bg-fog/30 rounded-2xl border border-fog/60 overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full text-left px-6 py-5 flex items-center justify-between gap-4">
                  <span className="font-medium text-pine text-sm">{faq.question}</span>
                  <span className={`text-amber flex-shrink-0 transition-transform duration-200 ${openFaq === index ? "rotate-45" : ""}`}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 5v14M5 12h14" />
                    </svg>
                  </span>
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-5">
                    <p className="text-sm text-pine-light font-light leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}